// ---------------------------------------------------------------------------
// UI helpers — loading overlay, toasts, modal, connection status, placeholders
// ---------------------------------------------------------------------------

import { STATE } from './config';

// ── Loading overlay ─────────────────────────────────────────────────────────

export function showLoading(message = 'Loading...'): void {
  const text = document.getElementById('loadingText');
  const overlay = document.getElementById('loadingOverlay');
  if (text) text.textContent = message;
  overlay?.classList.add('active');
}

export function hideLoading(): void {
  document.getElementById('loadingOverlay')?.classList.remove('active');
}

// ── Toast notifications ─────────────────────────────────────────────────────

const TOAST_ICONS: Record<string, string> = {
  success: 'fas fa-check-circle',
  error:   'fas fa-exclamation-circle',
  warning: 'fas fa-exclamation-triangle',
  info:    'fas fa-info-circle',
};

export function showToast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
  const toast = document.getElementById('toast');
  if (!toast) return;
  const icon = toast.querySelector('.toast-icon') as HTMLElement | null;
  const msgEl = toast.querySelector('.toast-message') as HTMLElement | null;
  if (icon) icon.className = `toast-icon ${TOAST_ICONS[type]}`;
  if (msgEl) msgEl.textContent = message;
  toast.className = `toast ${type}`;
  toast.classList.add('active');
  setTimeout(() => toast.classList.remove('active'), 5_000);
}

// ── Modal helpers ───────────────────────────────────────────────────────────

const MODAL_CSS = 'display:flex !important;position:fixed !important;top:0 !important;left:0 !important;' +
  'width:100% !important;height:100% !important;z-index:9999 !important;' +
  'background:rgba(0,0,0,0.7) !important;align-items:center !important;justify-content:center !important;';

export function openModal(title: string, contentHTML: string): void {
  const titleEl   = document.getElementById('modalTrendTitle');
  const contentEl = document.getElementById('modalTrendContent');
  const modal     = document.getElementById('trendModal');
  if (!titleEl || !contentEl || !modal) return;

  titleEl.textContent = title;
  contentEl.innerHTML = contentHTML;
  modal.classList.add('active');
  modal.style.cssText = MODAL_CSS;
  document.body.style.overflow = 'hidden';
}

export function closeTrendModal(): void {
  const modal = document.getElementById('trendModal');
  if (modal) {
    modal.classList.remove('active');
    modal.style.display = 'none';
  }
  document.body.style.overflow = '';
}

// ── Connection status ───────────────────────────────────────────────────────

export function updateConnectionStatus(connected: boolean): void {
  STATE.isConnected = connected;
  const el = document.getElementById('connectionStatus');
  if (!el) return;
  el.classList.toggle('connected', connected);
  el.classList.toggle('disconnected', !connected);
  el.title = connected ? 'Connected to API' : 'Disconnected from API';
}

// ── Section placeholder helpers ─────────────────────────────────────────────

export function clearDashboardData(): void {
  const ids = ['totalTrends', 'totalTopics', 'totalVolume'];
  ids.forEach(id => { const el = document.getElementById(id); if (el) el.textContent = '0'; });
  const sentEl = document.getElementById('avgSentiment');
  if (sentEl) sentEl.textContent = '0.0';

  setPlaceholder('dashboardTopicsGrid', 'fa-spinner fa-spin', 'Loading data...');
  setPlaceholder('recentTrendsList', 'fa-spinner fa-spin', 'Loading...', 'activity-placeholder');
  ['spikesChart', 'sentimentChart', 'campaignChart'].forEach(id =>
    setPlaceholder(id, 'fa-spinner fa-spin', 'Loading chart...', 'chart-placeholder'));
}

export function showTrendsPlaceholder(message: string): void {
  const container = document.getElementById('trendsGrid');
  if (!container) return;
  container.innerHTML = `
    <div class="trends-placeholder">
      <i class="fas fa-trending-up"></i>
      <p>${message}</p>
      <div style="margin-top:20px;padding:15px;background:#f7fafc;border-radius:8px;text-align:left;max-width:600px">
        <h4 style="margin:0 0 10px 0;color:#2d3748">About Trends</h4>
        <p style="margin:0;font-size:0.9em;color:#4a5568">
          <strong>Trends</strong> detect sudden spikes in topic volume by comparing today vs. previous days.
          Currently showing <strong>emerging topics</strong> since we need multiple days of data for spike detection.
        </p>
      </div>
    </div>`;
}

export function showTopicsPlaceholder(message: string): void {
  const isDashboard = STATE.currentSection === 'dashboard';
  const id = isDashboard ? 'dashboardTopicsGrid' : 'topicsGrid';
  setPlaceholder(id, 'fa-tags', message);
}

export function showAnalyticsPlaceholder(message: string): void {
  ['eventVolumeChart', 'evolutionChart', 'sentimentEvolution', 'campaignEvolution'].forEach(id =>
    setPlaceholder(id, 'fa-chart-line', message, 'chart-placeholder'));
}

/** Replace chart/list spinners with static error placeholders when dashboard load fails */
export function showDashboardErrorPlaceholder(message = 'Failed to load data'): void {
  setPlaceholder('dashboardTopicsGrid', 'fa-exclamation-triangle', message);
  setPlaceholder('recentTrendsList', 'fa-inbox', message, 'activity-placeholder');
  setPlaceholder('spikesChart', 'fa-chart-line', message, 'chart-placeholder');
  setPlaceholder('sentimentChart', 'fa-chart-pie', message, 'chart-placeholder');
  setPlaceholder('campaignChart', 'fa-tags', message, 'chart-placeholder');
}

function setPlaceholder(containerId: string, icon: string, text: string, cls = 'topics-placeholder'): void {
  const el = document.getElementById(containerId);
  if (el) el.innerHTML = `<div class="${cls}"><i class="fas ${icon}"></i><p>${text}</p></div>`;
}

// ── API error handler ───────────────────────────────────────────────────────

export function handleAPIError(error: AxiosError): void {
  let message = 'An unexpected error occurred';
  if (error.response) {
    message = error.response.data?.detail || error.response.data?.message || `Server error: ${error.response.status}`;
  } else if (error.request) {
    const code = (error as AxiosError & { code?: string }).code;
    if (code === 'ECONNABORTED') {
      message = 'Request timed out — the API may be busy. Wait a moment and refresh, or restart the API on port 8000.';
    } else if (code === 'ERR_NETWORK') {
      message = 'Network error: Unable to connect to server. Open http://localhost:5000 and ensure the API is running on port 8000.';
    } else {
      message = 'Network error: Unable to connect to server. Open http://localhost:5000 and ensure the API is running on port 8000.';
    }
  } else {
    message = error.message || message;
  }
  console.error('API Error:', error);
  showToast(message, 'error');
}

